import { asArray, isDateGreater, isDateLess, isRealDate } from './promoPrimitives';
import { getOfferInfo, refOfOfferInfo } from './promoModel';
import { PHASE, offerPhase, rawLevels } from './promoPhase';
import { offerInfoOf } from './promoOfferInfo';

// Forecast mode of the promo offers (#23521 plan 2, spec §8 / §9, RG-14 → RG-18, RG-24, RG-25, RG-27 → RG-29).
// While an offer is in forecast, its negotiation is a forecast : no order, no points validation, no statement.
// - a national offer is in forecast during the forecast period of its campaign only, locked ; never outside it ;
// - a local ("pirate") offer is in forecast by default, whatever the period, until the sales rep switches it off for good.
// The state is stored by offer, in the visit (`AppointmentOfferInfo.ForecastMode`, #23555) ; the campaign has no forecast option.

/** H2 : the forecast period replaces the unused appro period of the campaign (`approStartDate` / `approEndDate`). */
export const getForecastPeriod = (campaign) => ({ start: campaign?.dates?.forecastStart || null, end: campaign?.dates?.forecastEnd || null });

/** RG-17 / RG-18 : a national campaign is in its forecast period. A local campaign ignores it. */
export function isInForecastPeriod(campaign, date) {
	if (campaign?.promoLocal) return false;
	const { start, end } = getForecastPeriod(campaign);
	return isRealDate(start) && isRealDate(end) && isDateLess(start, date, true) && isDateGreater(end, date, true);
}

export const LOCK = Object.freeze({ PERIOD: 'period', DEACTIVATED: 'deactivated' });

/**
 * Whether a local offer left its forecast : switched off (RG-16, `forecastMode: false`), or already stated. No presence
 * can be set while the forecast is on (§8), so a stated offer was switched off ; a new visit takes the offer over from
 * the previous one, its forecast state and its statement included.
 */
const isLocalForecastOff = (offerInfo) => offerInfo?.forecastMode === false || asArray(offerInfo?._statedCampaignOffers).some((row) => row.present > 0);

/**
 * State of the forecast switch of an offer : `{ enabled, visible, active, locked, lockReason }`.
 * - national offer : on and locked during the forecast period (RG-17), no forecast at all outside it ;
 * - local offer : on by default, until switched off for good, the banner disappearing then (RG-16, Q3).
 */
export function getOfferForecastState(snapshot, campaign, ref) {
	if (campaign?.promoLocal) {
		if (isLocalForecastOff(offerInfoOf(snapshot, ref))) return { enabled: true, visible: false, active: false, locked: true, lockReason: LOCK.DEACTIVATED };
		return { enabled: true, visible: true, active: true, locked: !!snapshot?.readonly, lockReason: null };
	}
	if (isInForecastPeriod(campaign, snapshot?.now)) return { enabled: true, visible: true, active: true, locked: true, lockReason: LOCK.PERIOD };
	return { enabled: false, visible: false, active: false, locked: false, lockReason: null };
}

/**
 * Whether the sales rep may switch the forecast off (the UI then confirms it, the action being irreversible, RG-16) :
 * refused during the forecast period (RG-17 : the switch is locked, so disabled, tester feedback 2026-10-27) and, for a
 * local offer in its store period, while its end date is past (RG-25, dialog "Date de fin dépassée").
 */
export function canDeactivateForecast(snapshot, campaign, ref) {
	const state = getOfferForecastState(snapshot, campaign, ref);
	if (!state.active) return { ok: false, reason: null };
	if (state.lockReason === LOCK.PERIOD) return { ok: false, reason: 'PromoForecastLocked' };
	if (snapshot?.readonly) return { ok: false, reason: null };
	if (campaign?.promoLocal) {
		const offerInfo = getOfferInfo(snapshot, ref.offerKey);
		const inStore = isDateLess(offerInfo?.startDate, snapshot?.now, true);
		if (inStore && isRealDate(offerInfo?.endDate) && isDateLess(offerInfo.endDate, snapshot?.now)) return { ok: false, reason: 'PromoOfferEndDatePassed' };
	}
	return { ok: true, reason: null };
}

/** RG-16 / RG-27 : the switch off is stored on the offer, in the visit. */
export const deactivateForecastPatch = (ref) => ({ scope: ref, offerInfo: { forecastMode: false } });

/**
 * RG-27 : once an offer is worked on, its forecast state is stored on it (the BO reporting shows it, and the next visit
 * takes it over). Returns the offerInfo fields to add to the commit, or null when the stored state is already right ; a
 * national offer out of forecast gets no offerInfo only for it.
 */
export function forecastRecordPatch(snapshot, campaign, ref) {
	if (snapshot?.readonly) return null;
	const { active } = getOfferForecastState(snapshot, campaign, ref);
	const stored = offerInfoOf(snapshot, ref)?.forecastMode;
	if (stored === active || (!active && typeof stored !== 'boolean')) return null;
	return { forecastMode: active };
}

/** RG-14 : while the forecast is on, no order : the Commander button and the cart icons are absent. */
export const isOrderBlocked = (state) => !!state?.active;

/** RG-15 : while the forecast is on, the points cannot be validated ("Valider mes points" hidden). */
export const isPointsValidationBlocked = (state) => !!state?.active;

/**
 * Display phase of an offer once the forecast is taken into account. RG-24 : a local offer in its store period stays in
 * the negotiation display while its forecast is on, everything editable, dates included ; once off, it switches to the
 * statement display with frozen dates. A national offer in its store period keeps the statement display, blocked.
 */
export function displayPhase(snapshot, campaign, ref) {
	const phase = offerPhase(snapshot, campaign, ref.offerKey);
	if (campaign?.promoLocal && phase === PHASE.STATEMENT && getOfferForecastState(snapshot, campaign, ref).active) return PHASE.NEGO;
	return phase;
}

/**
 * RG-24 on the raw (date-based) levels : a local offer in its store period is still a negotiation while its forecast is
 * on. The status bars and the points context follow it, as the display does.
 */
export function isLocalForecastNego(snapshot, campaign, ref) {
	if (!campaign?.promoLocal || !ref?.offerKey) return false;
	const { nego, store } = rawLevels(snapshot, campaign, ref.offerKey);
	return !nego && store > 0 && getOfferForecastState(snapshot, campaign, ref).active;
}

/**
 * Whether the campaign, or the local offer, is still rated on its negotiation : its nego level runs, or its forecast keeps
 * it in the negotiation (RG-24). The status bars follow it, and so do the nego rules of a local offer : once in its
 * statement, its negotiation is frozen and they no longer block the visit (#23572).
 */
export function isRatedOnNego(snapshot, campaign, offerInfo = null) {
	if (rawLevels(snapshot, campaign, campaign.promoLocal && offerInfo ? offerInfo._key : null).nego > 0) return true;
	return !!offerInfo && isLocalForecastNego(snapshot, campaign, refOfOfferInfo(offerInfo));
}

/** §8 "Blocage du constat" : in the store period, while the forecast is on, no presence can be set. */
export const isStatementBlocked = (snapshot, campaign, ref) => displayPhase(snapshot, campaign, ref) === PHASE.STATEMENT && getOfferForecastState(snapshot, campaign, ref).active;

/** The local offer dates stay editable in the store period while its forecast is on (RG-24). */
export function forecastKeepsDatesEditable(snapshot, campaign, ref) {
	if (!campaign?.promoLocal || snapshot?.readonly) return false;
	return offerPhase(snapshot, campaign, ref.offerKey) === PHASE.STATEMENT && getOfferForecastState(snapshot, campaign, ref).active;
}

/** Banner text of the switch, per situation (mockups figures 2, 3, 9, 12, 14, 15). */
export function forecastHintKey(snapshot, campaign, ref) {
	const state = getOfferForecastState(snapshot, campaign, ref);
	if (state.lockReason === LOCK.PERIOD) return 'PromoForecastPeriodHint';
	if (offerPhase(snapshot, campaign, ref.offerKey) === PHASE.STATEMENT) return 'PromoForecastLocalStoreHint';
	return 'PromoForecastNegoHint';
}

/** Legacy adapter (sfaHelper.campaign negoPeriodLevel / getActivePromos) : a legacy campaignModel carries the forecast period on the appro dates (H2). */
export const isLegacyCampaignInForecastPeriod = (legacyCampaign, date) => isInForecastPeriod({ promoLocal: legacyCampaign?.promoLocal, dates: { forecastStart: legacyCampaign?.approStartDate, forecastEnd: legacyCampaign?.approEndDate } }, date);
